import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CurrencySelect } from "@/components/CurrencySelect";
import { CaretLeft, CaretRight, TrendUp, CalendarPlus, ChartLineUp, CurrencyCircleDollar, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Link } from "react-router-dom";

const fmtRange = (s, e) => {
  try {
    const a = new Date(s + "T00:00:00"), b = new Date(e + "T00:00:00");
    return `${format(a, "MMM d")} – ${format(b, "MMM d, yyyy")}`;
  } catch { return `${s} – ${e}`; }
};

export default function Projections() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const currency = user?.currency || "CAD";
  const [idx, setIdx] = useState(0);
  const display = user?.projection_display === "gross" ? "gross" : "net";

  const [fx, setFx] = useState(null); // {target, rate, date}
  const convert = useMutation({
    mutationFn: async (target) => (await api.get(`/fx/rate?base=${currency}&target=${target}`)).data,
    onSuccess: (d) => { setFx({ target: d.target, rate: d.rate, date: d.date }); toast.success(`Converted to ${d.target} @ ${d.rate}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not fetch exchange rate."),
  });
  const disp = (val) => fx ? money((val || 0) * fx.rate, fx.target) : money(val, currency);

  const { data, isLoading } = useQuery({
    queryKey: ["projections"],
    queryFn: async () => (await api.get("/projections?count=13")).data,
  });

  const setDisplay = useMutation({
    mutationFn: async (mode) => (await api.put("/settings", { projection_display: mode })).data,
    onSuccess: (u) => { setUser(u); qc.invalidateQueries({ queryKey: ["projections"] }); },
  });

  if (isLoading || !data) {
    return <div className="animate-fade-up text-muted-foreground" data-testid="projections-loading">Loading projections…</div>;
  }

  const isGross = display === "gross";
  const bigVal = (p) => (isGross ? p.gross_pay : p.net_pay);
  const periods = data.periods || [];
  const p = periods[idx] || periods[0];
  const earnings = [
    ["Base Pay", p.base_pay], ["Regular", p.regular_pay], ["Overtime", p.overtime_pay],
    ["Paid Duty", p.paid_duty_pay], ["Court", p.court_pay], ["Other", p.other_pay],
    ...Object.entries(p.custom_by_type || {}),
  ].filter(([, v]) => v);

  return (
    <div className="animate-fade-up" data-testid="projections-page">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <ChartLineUp className="h-5 w-5 text-primary" weight="bold" />
          <p className="overline text-primary">Forecast</p>
        </div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Pay Period Projections</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Projected pay for upcoming cycles from your scheduled work, deductions and payment-timing settings.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <div className="inline-flex rounded-md border border-border p-0.5 bg-secondary/40" data-testid="proj-display-toggle">
            {[["net", "Net pay"], ["gross", "Gross pay"]].map(([mode, label]) => (
              <button key={mode} onClick={() => setDisplay.mutate(mode)} data-testid={`proj-display-${mode}`}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors duration-200 ${
                  display === mode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          <Link to="/pay-changes" data-testid="proj-pay-changes-link" className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1">
            <CalendarPlus className="h-3.5 w-3.5" weight="bold" /> Schedule a future pay change
          </Link>
        </div>

        {/* Currency conversion */}
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="proj-convert-bar">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CurrencyCircleDollar className="h-4 w-4 text-emerald-400" weight="bold" /> Convert earnings to
          </div>
          <div className="w-56">
            <CurrencySelect value={fx?.target || currency} onChange={(code) => { if (code === currency) { setFx(null); } else { convert.mutate(code); } }} />
          </div>
          {fx && (
            <div className="flex items-center gap-2 text-xs" data-testid="proj-fx-note">
              <span className="text-muted-foreground">1 {currency} = {fx.rate} {fx.target}{fx.date ? ` · ${fx.date}` : ""}</span>
              <button onClick={() => setFx(null)} data-testid="proj-fx-clear" className="inline-flex items-center gap-1 text-rose-400 hover:underline">
                <X className="h-3 w-3" weight="bold" /> Show in {currency}
              </button>
            </div>
          )}
          {convert.isPending && <span className="text-xs text-muted-foreground" data-testid="proj-fx-loading">Fetching rate…</span>}
        </div>
      </div>

      {/* Period navigator */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <Button variant="outline" size="icon" className="rounded-md" data-testid="proj-prev"
          disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
          <CaretLeft className="h-4 w-4" weight="bold" />
        </Button>
        <div className="text-center">
          <p className="font-bold" data-testid="proj-range">{fmtRange(p.period_start, p.period_end)}</p>
          <p className="text-xs text-muted-foreground" data-testid="proj-label">
            {p.is_current ? "Current pay period" : idx === 1 ? "Next paycheck" : `${idx} periods ahead`}
          </p>
        </div>
        <Button variant="outline" size="icon" className="rounded-md" data-testid="proj-next"
          disabled={idx >= periods.length - 1} onClick={() => setIdx((i) => Math.min(periods.length - 1, i + 1))}>
          <CaretRight className="h-4 w-4" weight="bold" />
        </Button>
      </div>

      {/* Main projection card */}
      <Card className="p-8 mb-6 bg-primary/10 border-primary/30 relative overflow-hidden" data-testid="proj-card">
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-emerald-500/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <TrendUp className="h-5 w-5 text-emerald-400" weight="bold" />
            <span className="overline text-emerald-400">Projected {isGross ? "Gross" : "Net"} Pay</span>
            {p.scheduled_count > 0 && (
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-400" data-testid="proj-scheduled-count">
                {p.scheduled_count} scheduled
              </span>
            )}
            {p.changes_applied > 0 && (
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300" data-testid="proj-changes-applied">
                {p.changes_applied} pay change{p.changes_applied > 1 ? "s" : ""} applied
              </span>
            )}
          </div>
          <p className="text-5xl sm:text-6xl font-black tracking-tight text-emerald-400" data-testid="proj-net-pay">
            {disp(bigVal(p))}
          </p>
          <div className="mt-4 space-y-1 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="w-32">Gross Pay:</span>
              <span className="font-semibold text-foreground" data-testid="proj-gross">{disp(p.gross_pay)}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="w-32">Total Deductions:</span>
              <span className="font-semibold text-rose-400" data-testid="proj-deductions">−{disp(p.total_deductions)}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="w-32">Net Pay:</span>
              <span className="font-semibold text-emerald-400" data-testid="proj-net-line">{disp(p.net_pay)}</span>
            </div>
          </div>
        </div>
      </Card>

      {p.gross_pay === 0 ? (
        <Card className="p-6 bg-card border-border text-center" data-testid="proj-empty">
          <p className="text-muted-foreground text-sm">No pay projected for this period yet.</p>
          <Link to="/calendar" className="text-primary text-sm hover:underline" data-testid="proj-schedule-link">
            Schedule shifts on the calendar →
          </Link>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-5 bg-card border-border">
            <p className="overline text-emerald-400 mb-3">Earnings</p>
            {earnings.map(([label, val]) => (
              <div key={label} className="flex justify-between text-sm py-1">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-semibold">{disp(val)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm py-2 border-t border-border mt-1 font-bold">
              <span>Gross Pay</span><span>{disp(p.gross_pay)}</span>
            </div>
          </Card>
          <Card className="p-5 bg-card border-border">
            <p className="overline text-rose-400 mb-3">Deductions</p>
            {(p.deduction_lines || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No active deductions.</p>
            ) : (
              <>
                {p.deduction_lines.map((d) => (
                  <div key={d.id} className="flex justify-between text-sm py-1">
                    <span className="text-muted-foreground">{d.name}{d.kind === "percent" ? ` (${d.value}%)` : ""}</span>
                    <span className="font-semibold text-rose-400">−{disp(d.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm py-2 border-t border-border mt-1 font-bold text-rose-400">
                  <span>Total</span><span>−{disp(p.total_deductions)}</span>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Upcoming strip */}
      <p className="overline text-muted-foreground mt-8 mb-3">Upcoming paychecks</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="proj-strip">
        {periods.map((per, i) => (
          <button key={per.offset} onClick={() => setIdx(i)} data-testid={`proj-chip-${per.offset}`}
            className={`text-left p-3 rounded-md border transition-colors duration-200 ${
              i === idx ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"}`}>
            <p className="text-xs text-muted-foreground">{per.is_current ? "Current" : format(new Date(per.period_end + "T00:00:00"), "MMM d")}</p>
            <p className="font-black text-emerald-400 mt-0.5">{disp(isGross ? per.gross_pay : per.net_pay)}</p>
            <p className="text-[11px] text-muted-foreground">{isGross ? "gross" : "net"}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
